import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import { SendProposalInput, SendProposalMutation } from '@/gql/graphql';

const SEND_PROPOSAL_MUTATION = graphql(`
  mutation SendProposal($input: SendProposalInput!) {
    sendProposal(input: $input) {
      id
    }
  }
`);

/**
 * Send a Proposal to a Customer Contact by email. Sending transitions the
 * Proposal's status and touches Project-level state (pipeline, activity), so
 * both caches are invalidated on success.
 */
const useSendProposal = (args?: {
  proposalId?: string;
  projectId?: string;
  options?: UseMutationOptions<
    SendProposalMutation,
    unknown,
    SendProposalInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: SendProposalInput) => {
      return gqlClient.request(SEND_PROPOSAL_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.project(args.projectId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useSendProposal;
